import { useCallback, useRef, useState, useEffect } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { useNavigate } from "react-router-dom";
import type { GraphData, GraphNode } from "../../lib/api";
import { GraphControls } from "./GraphControls";
import { GraphFilters } from "./GraphFilters";
import { NodeDetailsPanel } from "./NodeDetailsPanel";
import {
  RenderedNode,
  RenderedLink,
  getNodeColor,
  getNodeSize,
  getNodeLabel,
  EDGE_COLORS,
  NODE_COLORS,
} from "./types";

interface AssetGraphProps {
  data: GraphData;
  width?: number;
  height?: number;
  onNodeClick?: (node: GraphNode) => void;
}

interface GraphFiltersState {
  assetTypes: string[];
  status: string | null;
  includePorts: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ForceGraphRef = any;

export function AssetGraph({
  data,
  width = 800,
  height = 600,
  onNodeClick,
}: AssetGraphProps) {
  const navigate = useNavigate();
  const graphRef = useRef<ForceGraphRef>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<GraphFiltersState>({
    assetTypes: [],
    status: null,
    includePorts: true,
  });

  // Filter graph data based on current filters
  const filteredData = useCallback(() => {
    let nodes = data.nodes;
    let edges = data.edges;

    // Filter by asset types
    if (filters.assetTypes.length > 0) {
      const types = new Set([...filters.assetTypes, "domain"]); // Always include domain
      nodes = nodes.filter((n) => types.has(n.node_type));
    }

    // Filter by status
    if (filters.status) {
      nodes = nodes.filter(
        (n) => n.status === filters.status || n.node_type === "domain"
      );
    }

    // Filter ports
    if (!filters.includePorts) {
      nodes = nodes.filter((n) => n.node_type !== "port");
    }

    // Filter edges to only include those with existing nodes
    const nodeIds = new Set(nodes.map((n) => n.id));
    edges = edges.filter(
      (e) => nodeIds.has(e.source) && nodeIds.has(e.target)
    );

    return {
      nodes: nodes as RenderedNode[],
      links: edges.map((e) => ({
        source: e.source,
        target: e.target,
        edge_type: e.edge_type,
      })) as RenderedLink[],
    };
  }, [data, filters]);

  const graphData = filteredData();

  // Handle node click
  const handleNodeClick = useCallback(
    (node: RenderedNode) => {
      setSelectedNode(node);
      if (onNodeClick) {
        onNodeClick(node);
      }
    },
    [onNodeClick]
  );

  // Navigate to asset details
  const handleNavigateToAsset = useCallback(
    (nodeId: string) => {
      navigate(`/assets/${nodeId}`);
    },
    [navigate]
  );

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    graphRef.current?.zoom(1.5, 400);
  }, []);

  const handleZoomOut = useCallback(() => {
    graphRef.current?.zoom(0.67, 400);
  }, []);

  const handleFitView = useCallback(() => {
    graphRef.current?.zoomToFit(400, 50);
  }, []);

  const handleReset = useCallback(() => {
    graphRef.current?.centerAt(0, 0, 400);
    graphRef.current?.zoom(1, 400);
  }, []);

  // Auto-fit on data change
  useEffect(() => {
    const timer = setTimeout(() => {
      graphRef.current?.zoomToFit(400, 50);
    }, 500);
    return () => clearTimeout(timer);
  }, [data]);

  // Custom node rendering
  const nodeCanvasObject = useCallback(
    (node: RenderedNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const label = getNodeLabel(node);
      const fontSize = 12 / globalScale;
      const nodeSize = getNodeSize(node);
      const color = getNodeColor(node);

      // Draw node circle
      ctx.beginPath();
      ctx.arc(node.x || 0, node.y || 0, nodeSize, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      // Draw border for selected node
      if (selectedNode?.id === node.id) {
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2 / globalScale;
        ctx.stroke();
      }

      // Draw label at higher zoom levels
      if (globalScale > 0.6) {
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        // Use a text shadow for better readability
        ctx.save();
        ctx.fillStyle = "#000";
        ctx.globalAlpha = 0.5;
        ctx.fillText(label, (node.x || 0) + 0.5, (node.y || 0) + nodeSize + 2.5);
        ctx.restore();
        ctx.fillStyle = "#fff";
        ctx.fillText(label, node.x || 0, (node.y || 0) + nodeSize + 2);
      }
    },
    [selectedNode]
  );

  // Custom link rendering
  const linkCanvasObject = useCallback(
    (link: RenderedLink, ctx: CanvasRenderingContext2D) => {
      const source = link.source as RenderedNode;
      const target = link.target as RenderedNode;

      if (!source.x || !source.y || !target.x || !target.y) return;

      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.strokeStyle = EDGE_COLORS[link.edge_type] || "#94a3b8";
      ctx.lineWidth = link.edge_type === "cname_to" ? 1.5 : 1;

      // Dashed line for CNAME
      if (link.edge_type === "cname_to") {
        ctx.setLineDash([4, 2]);
      } else {
        ctx.setLineDash([]);
      }

      ctx.stroke();
      ctx.setLineDash([]);
    },
    []
  );

  // Node pointer area paint
  const nodePointerAreaPaint = useCallback(
    (node: RenderedNode, color: string, ctx: CanvasRenderingContext2D) => {
      const size = getNodeSize(node);
      ctx.beginPath();
      ctx.arc(node.x || 0, node.y || 0, size + 4, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
    },
    []
  );

  return (
    <div className="relative bg-background rounded-lg border border-border overflow-hidden">
      {/* Graph Canvas */}
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        width={width}
        height={height}
        nodeId="id"
        nodeLabel={((node: RenderedNode) => node.label) as never}
        nodeCanvasObject={nodeCanvasObject as never}
        nodePointerAreaPaint={nodePointerAreaPaint as never}
        linkCanvasObject={linkCanvasObject as never}
        onNodeClick={handleNodeClick as never}
        linkDirectionalArrowLength={4}
        linkDirectionalArrowRelPos={1}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
        warmupTicks={50}
        cooldownTicks={100}
        enableNodeDrag={true}
        enableZoomInteraction={true}
        enablePanInteraction={true}
        backgroundColor="transparent"
      />

      {/* Controls */}
      <GraphControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFitView={handleFitView}
        onReset={handleReset}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {/* Filters Panel */}
      {showFilters && (
        <GraphFilters
          filters={filters}
          onFiltersChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Node Details Panel */}
      <NodeDetailsPanel
        node={selectedNode}
        onClose={() => setSelectedNode(null)}
        onNavigate={handleNavigateToAsset}
      />

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-card/90 backdrop-blur-sm border border-border rounded-lg p-3 z-10">
        <p className="text-xs font-medium mb-2">Legend</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {Object.entries(NODE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-muted-foreground capitalize">{type}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 pt-2 border-t border-border">
          <div className="flex items-center gap-1.5">
            <span className="w-4 border-t border-slate-400" />
            <span className="text-xs text-muted-foreground">Parent</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 border-t border-dashed border-amber-500" />
            <span className="text-xs text-muted-foreground">CNAME</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="absolute bottom-4 right-4 bg-card/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2 z-10">
        <div className="flex gap-4 text-xs">
          <span className="text-muted-foreground">
            Nodes: <span className="font-medium text-foreground">{graphData.nodes.length}</span>
          </span>
          <span className="text-muted-foreground">
            Edges: <span className="font-medium text-foreground">{graphData.links.length}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
